import { inject, Injectable, signal } from '@angular/core';
import { Company, COMPANY_STATE } from '../interface/company';
import { MkgUser } from '../interface/user';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, of, tap } from 'rxjs';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { DocumentPipe } from '../shared/pipes/document.pipe';
import { AccessProfile } from '../component/page/company/access-profile/models/access-profile';
import { ClientIndexedDbService } from './client-indexed-db.service';
import { PhonePipe } from '@app/shared/pipes/phone.pipe';
import { RoEndedIndexedDbService } from './ro-ended-indexed-db.service';
import { AccessProfileStatus } from '../interface/profile-access-state';

@Injectable({
  providedIn: 'root'
})
export class DataService {

  indexedDbService = inject(ClientIndexedDbService);
  endedRoIndexedDbService = inject(RoEndedIndexedDbService);

  /** Used to alert that there are series equal */
  areSerieEqualsSgn = signal(false);

  get user(): MkgUser {
    return this.user$.value;
  }
  user$ = new BehaviorSubject<MkgUser>(null);

  get company(): Company {
    return this.company$.value;
  }
  company$ = new BehaviorSubject<Company>(null);

  ready$ = combineLatest([this.user$.pipe(distinctUntilChanged()), this.company$.pipe(distinctUntilChanged())])
    .pipe(
      tap(([user, company]) => {
        try { // Prepare data for CRM widget, if user is logged in
          if (!user) return;
          const crmStr = localStorage.getItem('tecincocrm_widget_user_data');
          const crmData = JSON.parse(crmStr || '{}');
          localStorage.setItem('tecincocrm_widget_user_data', JSON.stringify({
            ...crmData,
            name: user.name,
            phone: new PhonePipe().transform(user.phone1 || user.phone2),
            email: user.email,
            message: crmData?.message || ""
          }));
        } catch (error) {
          console.error('Error parsing CRM data from localStorage', error);
        }

        // Check if series are equal and alert the user (only if company is migrated integrated)
        let areSeriesEquals = false;
        if (company && company.migrateIntegrated) {
          const seriesSet = new Set();
          let emptySeries = 0; // Dont count empty series
          const serieFieldNames = ['serieInvoice', 'serieInvoiceNFC', 'serieInvoiceNFS', 'serieGerencial'];
          for (const fieldName of serieFieldNames) {
            const seriesValue = company[fieldName];
            if (seriesValue) {
              seriesSet.add(seriesValue);
            } else {
              emptySeries++;
            }
          }
          areSeriesEquals = seriesSet.size < serieFieldNames.length - emptySeries;
        }
        this.areSerieEqualsSgn.set(areSeriesEquals);
      }),
      filter(([user, company]) => Boolean(user) && Boolean(company))
    );

  accessProfile: AccessProfile = undefined; // used by ProfileAccessGuard
  accessProfile$ = new BehaviorSubject<AccessProfile>(undefined); // used by canAccessPipe
  accessProfileStatus$ = new BehaviorSubject<AccessProfileStatus>('pending');

  userRole: number;
  userRole$ = combineLatest([this.user$, this.company$])
    .pipe(
      map(([user, company]) => {
        if (user && company) {
          return user.getRoleIn(company);
        } else {
          return undefined;
        }
      })
    )

  constructor() {
    this.company$.subscribe(c => {
      // this.company = c;
      if (c && c?.currentState !== COMPANY_STATE.blocked) {
        this.indexedDbService.localStoreClients(c);
        this.endedRoIndexedDbService.syncCompany(c);
      }

      return c;
    });
    // this.user$.subscribe(u => this.user = u);
    this.userRole$.subscribe(r => this.userRole = r);
    this.accessProfile$.subscribe(p => this.accessProfile = p);
  }

  // Cache to store if the company is a matrix with branches
  private _isMultiBranchMatrix: boolean | null = null;

  /**
   * Check if the current company is part of a multi-branch structure
   * (either a matrix with branches or a branch with a matrix)
   * 
   * @returns true if company has cnpjMatrix set (is a branch) or is a matrix with branches
   */
  isMultiBranch(): boolean {
    if (!this.company) {
      return false;
    }
    // If company has cnpjMatrix set, it's a branch (part of multi-branch structure)
    if (this.company.cnpjMatrix && this.company.cnpjMatrix.trim().length > 0) {
      return true;
    }
    // If we've checked and the company is a matrix with branches
    if (this._isMultiBranchMatrix === true) {
      return true;
    }
    return false;
  }

  /**
   * Set the matrix status (to be called after checking with API)
   * @param hasFiliais true if the company is a matrix with branches
   */
  setMultiBranchMatrixStatus(hasFiliais: boolean): void {
    this._isMultiBranchMatrix = hasFiliais;
  }

  /**
   * Check if the matrix status has been verified
   */
  isMultiBranchMatrixChecked(): boolean {
    return this._isMultiBranchMatrix !== null;
  }

  httpOptions(fromMatrix: boolean | string, params?: HttpParams, contentType = "application/json; charset=utf-8") {
    if (typeof fromMatrix == 'string' && fromMatrix.trim().length > 0) {
      const options: {
        headers: HttpHeaders,
        params?: HttpParams
      } = {
        headers: new HttpHeaders()
          .append("Company-Cnpj", new DocumentPipe().transform(fromMatrix))
          .append("Content-Type", contentType)
      }
      if (params) {
        options.params = params;
      }
      return of(options);
    }

    return this.ready$.pipe(
      map(([_user, company]) => company),
      map(company => {
        let cnpj = company.cnpj;
        if (fromMatrix) {
          if (company.cnpjMatrix && company.cnpjMatrix.trim().length > 0) {
            cnpj = company.cnpjMatrix;
          }
        }
        const options: {
          headers: HttpHeaders,
          params?: HttpParams
        } = {
          headers: new HttpHeaders()
            .append("Company-Cnpj", new DocumentPipe().transform(cnpj))
            .append("Content-Type", contentType)
        }
        if (params) {
          options.params = params;
        }
        return options;
      })
    )
  }

}
